// Session-scoped cache of the PrivateRoute auth/licensing decision. It is purely
// an optimisation: any miss, corruption, version mismatch or expiry returns null
// and the caller falls through to the full (authoritative) auth flow — so a bad
// cache can never wrongly grant/deny access, only cost one extra round-trip.

const AUTH_CACHE_KEY = "app_auth_cache";
// Bump to invalidate every previously-cached decision (e.g. after changing the
// licensing logic, or on a breaking deploy).
// v3: identity (email/fullName) is now sourced from Graph, not OIDC claims —
// invalidate v2 entries that may have cached a blank email/name.
// v4: adds `supportTenant`. A v3 entry has no saved tenant, so a CORP user holding
// one would be left un-impersonated for the rest of the cache TTL after the deploy;
// bumping forces one authoritative pass that fetches it.
// v5: userInfo now carries `supportBootstrap`. A v4 entry cannot say whether the user
// has a row of their own, so a bootstrapped session restored from one would drop the
// SUPPORT_CORPUSER override and show an empty application until the entry expired.
export const CACHE_VERSION = 5;
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface AuthCachePayload {
  isAuthenticated: boolean;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  company: string;
  // Opaque backend user record; kept as `any` to match the store's setInfo() input
  // (this is the shape PrivateRoute already round-tripped through the cache).
  userInfo: any;
  // Org code a CORP/CORP2 user is currently connected to as its support user, so a page
  // reload re-applies the impersonation without another backend round-trip (impersonation
  // is module state, which a reload clears — exactly like the active region). null/absent
  // for everyone else. Re-validated through resolveAutoTenant on the way out, so a stale
  // or tampered value can only ever be ignored, never widen access.
  supportTenant?: string | null;
}

/**
 * Parse + validate a raw cache string. Returns the payload ONLY when it is
 * well-formed, the current version and unexpired; otherwise null. Never throws
 * on bad input (previously a corrupt entry threw and left the app stuck on the
 * loading spinner).
 */
export function parseAuthCache(
  raw: string | null,
  now: number
): AuthCachePayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?._v !== CACHE_VERSION) return null;
    if (typeof parsed?._ts !== "number" || now - parsed._ts > CACHE_TTL_MS) {
      return null;
    }
    return {
      isAuthenticated: !!parsed.isAuthenticated,
      email: parsed.email ?? "",
      firstName: parsed.firstName ?? "",
      lastName: parsed.lastName ?? "",
      fullName: parsed.fullName ?? "",
      company: parsed.company ?? "",
      userInfo: parsed.userInfo,
      supportTenant: parsed.supportTenant ?? null,
    };
  } catch {
    return null;
  }
}

/** Read + validate the cached decision from sessionStorage (null if none/stale/corrupt). */
export function readAuthCache(now: number = Date.now()): AuthCachePayload | null {
  try {
    return parseAuthCache(sessionStorage.getItem(AUTH_CACHE_KEY), now);
  } catch {
    return null;
  }
}

/**
 * Drop the cached decision entirely. Called before starting an interactive login: the cache
 * TTL (30 minutes) outlives an access token, so a decision made with a now-dead token would
 * otherwise let the post-login mount skip straight past the authoritative flow.
 */
export function clearAuthCache(): void {
  try {
    sessionStorage.removeItem(AUTH_CACHE_KEY);
  } catch {
    /* storage unavailable — nothing cached to clear */
  }
}

/**
 * Update just the cached support tenant, leaving the rest of the decision alone. Used by
 * the org switcher so a reload straight after switching restores the same organization.
 *
 * A miss/stale entry is a no-op: the next mount runs the full flow, which re-reads the
 * saved tenant from the backend anyway. Note this re-stamps the entry's timestamp (it goes
 * through writeAuthCache), which is harmless — the user just interacted with the app.
 */
export function updateCachedSupportTenant(supportTenant: string | null): void {
  const cached = readAuthCache();
  if (!cached) return;
  writeAuthCache({ ...cached, supportTenant });
}

/** Persist the decision, stamped with the cache version + timestamp. Best-effort. */
export function writeAuthCache(
  payload: AuthCachePayload,
  now: number = Date.now()
): void {
  try {
    sessionStorage.setItem(
      AUTH_CACHE_KEY,
      JSON.stringify({ ...payload, _v: CACHE_VERSION, _ts: now })
    );
  } catch {
    /* storage unavailable/full — the cache is a best-effort optimisation */
  }
}
